import { useEffect, useState, useRef, useCallback } from "react"

interface UseCountUpOptions {
  start?: number
  end: number
  duration?: number
  delay?: number
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 10000) {
    return (num / 10000).toFixed(1).replace(/\.0$/, '') + '万'
  }
  if (num >= 1000) {
    return num.toLocaleString()
  }
  return Math.round(num).toString()
}

export function useCountUp({
  start = 0,
  end,
  duration = 2000,
  delay = 0,
}: UseCountUpOptions) {
  const [count, setCount] = useState(start)
  const [isAnimating, setIsAnimating] = useState(false)
  const hasAnimated = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const runAnimation = useCallback(() => {
    if (hasAnimated.current) return
    hasAnimated.current = true
    setTimeout(() => {
      setIsAnimating(true)
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
        const currentCount = start + (end - start) * easeOutExpo

        setCount(currentCount)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          setCount(end)
          setIsAnimating(false)
        }
      }

      requestAnimationFrame(animate)
    }, delay)
  }, [start, end, duration, delay])

  // callback ref: 当元素挂载/卸载时自动触发 observer
  const countRef = useCallback(
    (node: HTMLElement | null) => {
      // 清理旧 observer
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              runAnimation()
            }
          })
        },
        { threshold: 0.1 },
      )
      observer.observe(node)
      observerRef.current = observer
    },
    [runAnimation],
  )

  // 入场动画结束后，若 end 变化，直接同步
  useEffect(() => {
    if (hasAnimated.current && !isAnimating) {
      setCount(end)
    }
  }, [end, isAnimating])

  const formattedCount = formatNumber(count)

  return { count, formattedCount, isAnimating, countRef }
}
